import './CopyAndEditAttachesExecutor.scss';
import React, { useEffect, useRef, useState } from 'react';
import {
    ICopyAndEditAttachesAddResponse,
    ICopyAndEditAttachesData,
    ICopyAndEditAttachesScheme,
} from '@models/attaches/ICopyAndEditAttaches';
import { CopyAndEditAttachesService } from '@services/CopyAndEditAttachesService';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import { CopyAndEditAttachFilesBlock } from '@molecules/CopyAndEditAttachFilesBlock/CopyAndEditAttachFilesBlock';
import { stringifyError } from '@srm/core/errors';
import { ICopyAndEditAttachesExecutorProps } from './ICopyAndEditAttachesExecutorProps';

export const CopyAndEditAttachesExecutorModal: React.FC<ICopyAndEditAttachesExecutorProps> = ({
    docId,
    actionKey,
    displayName,
    okButtonText,
    cancelButtonText,
    completeHandler,
}) => {
    // Подготовка (схема и данные) и сохранение — разные состояния: подготовку можно бросить
    // закрытием окна, сохранение — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isBusy = loading || isSubmitting;
    const [errorText, setErrorText] = useState<string>();
    const [scheme, setScheme] = useState<ICopyAndEditAttachesScheme>();
    const [data, setData] = useState<ICopyAndEditAttachesData>();
    const [selected, setSelected] = useState<ICopyAndEditAttachesAddResponse[]>([]);
    const [existAttaches, setExistAttaches] = useState<Map<string, string[]>>(new Map());
    // Первое уведомление блока — стартовое выделение из данных; всё, что отличается от него, — правки.
    const initialSelection = useRef<string | undefined>(undefined);

    const close = () => completeHandler?.(false);

    useEffect(() => {
        // Закрытое окно ответы подготовки не применяет.
        let ignore = false;
        const failLoading = (error: unknown) => {
            if (ignore) return;
            setErrorText(stringifyError(error));
            setLoading(false);
        };
        setLoading(true);
        CopyAndEditAttachesService.fetchScheme('').then((x) => {
            if (!ignore) setScheme(x.data);
        }, failLoading);
        CopyAndEditAttachesService.fetchData(docId, '-1', '').then((x) => {
            if (!ignore) setData(x.data);
        }, failLoading);
        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (scheme && data) {
            const map = new Map();
            const list = data?.data.filter((x) => x.isExistAttach);
            list.forEach((item) => {
                map.set(item.key, item.existAttachKeys);
            });

            setExistAttaches(map);
            setLoading(false);
        }
    }, [scheme, data]);


    const onFilesSelected = (files: ICopyAndEditAttachesAddResponse[]) => {
        if (initialSelection.current === undefined) initialSelection.current = JSON.stringify(files);
        setSelected(files);
    };

    const onSubmit = () => {
        const addAttaches = selected.filter((file) => !existAttaches.has(file.key));

        const deleteAttaches = new Set<string>(); // Перечень attachId без галочки (должны быть удалены)
        const recoveryAttaches = new Set<string>(); // Перечень attachId с галочкой (нужно оставить)
        existAttaches.forEach((vals, key) => {
            if (selected.findIndex((file) => file.key == key) == -1) {
                vals.forEach(deleteAttaches.add, deleteAttaches);
            } else {
                vals.forEach(recoveryAttaches.add, recoveryAttaches);
            }
        });
        // Если для attachId стоит хотя бы одна галочка, то его стоит оставить
        recoveryAttaches.forEach(deleteAttaches.delete, deleteAttaches);

        const response = {
            addAttaches: addAttaches,
            deleteAttaches: Array.from(deleteAttaches),
        };

        setErrorText(undefined);
        setIsSubmitting(true);
        const executor = new DocumentExecutionService(docId);
        executor
            .copyAndEditAttaches(actionKey, response)
            .then(() => {
                if (completeHandler) completeHandler(true);
            })
            .catch((error) => setErrorText(error))
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={'max'}
            onClose={close}
            hasUnsavedInput={() =>
                initialSelection.current !== undefined && initialSelection.current !== JSON.stringify(selected)
            }
            closeDisabled={isSubmitting}
            header={displayName}
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        size="sm"
                        aria-label={okButtonText}
                        onClick={() => {
                            onSubmit();
                        }}
                        disabled={isBusy || !scheme || !data}
                    >
                        {okButtonText}
                    </Button>

                    <Button
                        variant="light"
                        size="sm"
                        aria-label={cancelButtonText}
                        onClick={close}
                        disabled={isSubmitting}
                    >
                        {cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth={'100%'} />}
                    {scheme && data && (
                        <CopyAndEditAttachFilesBlock scheme={scheme} data={data} onFilesSelected={onFilesSelected} />
                    )}
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
